import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { Button, Spinner, toast } from '@saas-kit/ui';

import { fetchDisplayMedia, uploadDisplayMedia } from './apiDisplays';

const ACCEPT = 'image/png,image/jpeg,image/webp';
// Mirrors the backend image ceiling (QMS_DISPLAY_IMAGE_MAX_BYTES).
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Logo picker for the display setup form. Uploads an image and stores its
 * file id; the public board re-signs that id into a fresh URL on every
 * poll, so the logo never breaks on an expired signature.
 */
export function DisplayLogoField({
  value,
  onChange,
}: {
  value: string;
  onChange: (fileId: string) => void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    void fetchDisplayMedia(value)
      .then((media) => {
        if (!cancelled) setPreviewUrl(media.download_url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  const pick = async (file: File): Promise<void> => {
    if (file.size > MAX_BYTES) {
      toast.error(t('displays.branding.too_large', { limit: 10 }));
      return;
    }
    setUploading(true);
    try {
      const media = await uploadDisplayMedia(file);
      onChange(media.id);
      setPreviewUrl(media.download_url);
    } catch (error) {
      toast.error(parseApiError(error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1 text-sm">
      <span className="text-foreground">{t('displays.form.logo')}</span>
      <div className="flex flex-wrap items-center gap-3">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t('displays.form.logo')}
            className="max-h-16 max-w-[10rem] rounded border border-border bg-white object-contain p-1"
            data-testid="display-logo-preview"
          />
        ) : (
          <div
            className="flex h-16 w-40 items-center justify-center rounded border border-dashed border-border text-xs text-muted-foreground"
            data-testid="display-logo-empty"
          >
            {t('displays.form.logo_empty')}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          data-testid="display-logo-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void pick(file);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Spinner size="sm" /> : null}
          {t(value ? 'displays.form.logo_replace' : 'displays.form.logo_upload')}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onChange('')}
            data-testid="display-logo-clear"
          >
            {t('displays.form.logo_clear')}
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">
        {t('displays.form.logo_hint')}
      </p>
    </div>
  );
}
